import * as React from 'react';
import { Link } from 'react-router';
import styled, { css } from 'styled-components';
import { rem } from 'polished';
import { __ } from 'react-i18n';
import { Button } from '../../Zemplin/General/Button';
import { FlexRowCenter } from '../../Defaults/Flexbox/Flexbox';
import { Loader } from '../../Zemplin/General/Loader';
import {
  FormInput,
  LoginButton,
  Register,
  StyledLabel,
  Wrapper,
} from './LoginForm';
import { __r } from 'react-i18n/lib';
import { DsiInfoText, DsiInfoType } from '../../..';

interface Props {
  onSendEmailClick: (e: string) => void;
  error: string | null;
  isFetching: boolean;
  emailSended: boolean;
  showLoginWithoutEmailInfo: boolean;
}

function ForgotPasswordForm({
  error,
  isFetching,
  emailSended,
  showLoginWithoutEmailInfo,
  onSendEmailClick,
}: Props) {
  const [email, setEmail] = React.useState('');
  const [isNotEmailMsg, setIsNotEmailMsg] = React.useState(false);

  const formRef = React.useRef(null);
  React.useEffect(() => {
    const handleKeyDown = event => {
      if (event.key === 'Enter') {
        handleSendEmailClick();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [email]);

  const handleSendEmailClick = () => {
    if (!email.includes('@')) {
      setIsNotEmailMsg(true);
      return;
    }

    onSendEmailClick(email);
  };

  return (
    <Wrapper>
      <Header>{`${__('Zabudnuté heslo')}`}</Header>
      <React.Fragment>
        {showLoginWithoutEmailInfo ? (
          <DsiInfoText
            type={DsiInfoType.INFO}
            info={__(
              'V prípade že na prihlasovanie nepoužívate e-mail, kontaktujte nás prosím ohľadom zmeny hesla telefonicky alebo emailom.',
            )}
          />
        ) : (
          <></>
        )}
        {!emailSended ? (
          <div>
            <StyledLabel htmlFor="email">
              {`${__('Prihlasovací email')}`}
            </StyledLabel>
            <FormInput
              tabIndex={1}
              type="email"
              name="email"
              value={email}
              // tslint:disable-next-line:jsx-no-lambda
              onChange={e => setEmail(e.target.value)}
            />

            {isNotEmailMsg && (
              <>
                <br />
                <DsiInfoText
                  marginBottom={0}
                  type={DsiInfoType.ERROR}
                  info={__(
                    'Pre zmenu hesla prosím kontaktujte svojho obchodného zástupcu alebo nám napíšte email na zakaznik@dsi.sk.',
                  )}
                />
              </>
            )}

            {error ? (
              <DsiInfoText
                marginBottom={0}
                type={DsiInfoType.ERROR}
                info={__(error)}
              />
            ) : (
              <></>
            )}

            <LoginButton
              tabIndex={2}
              type="submit"
              onClick={handleSendEmailClick}
            >
              {isFetching ? (
                <FlexRowCenter>
                  {`${__('Odosielam')}...`}
                  <Loader className="marginLeft" small={true} />
                </FlexRowCenter>
              ) : (
                __('Odoslať')
              )}
            </LoginButton>
          </div>
        ) : (
          <SuccessTextWrapper>
            {__('Email bol úspešne zaslaný')}
          </SuccessTextWrapper>
        )}

        <NoAccountWrapper>
          {emailSended ? (
            <LoginLink to={__r('routes:prihlasenie', '/prihlasenie')}>
              {__('Prihláste sa')}
            </LoginLink>
          ) : (
            <>
              {`${__('Nemáte účet')}? `}
              <Link to={__r('routes:registracia', '/registracia')}>
                <Register>{__('Vytvoriť účet')}</Register>
              </Link>
            </>
          )}
        </NoAccountWrapper>
      </React.Fragment>
    </Wrapper>
  );
}

const secondaryText = css`
  color: ${({ theme }) => theme.colors.textPrimary};
  text-decoration-color: ${({ theme }) => theme.colors.textPrimary};
  font-family: ${({ theme }) => theme.font.secondary};
  font-weight: 300;
  font-size: ${rem(14)};
  line-height: ${rem(22)};
`;

const Header = styled.h1`
  font-size: ${rem(30)};
  font-weight: 400;
  position: relative;
  font-family: ${({ theme }) => theme.font.primary};
  margin-bottom: ${rem(32)};
  line-height: ${rem(38)};
  color: ${({ theme }) => theme.colors.textPrimary};
`;
export const SubmitButton = styled(Button)`
  height: ${rem(48)};
  margin: ${rem(15)} 0 0;
  padding: ${rem(15)};
  width: 100%;
  max-width: ${rem(320)};
  background-color: ${({ theme }) => theme.colors.secondary};
  color: ${({ theme }) => theme.colors.black};
  font-size: ${rem(14)};
  border-radius: ${rem(2)};
  margin: ${rem(32)} 0;
  display: flex;
  justify-content: center;
  align-items: center;

  &:disabled {
    background-color: #dcd6d6;
    padding: 0;
  }
`;

const LoginLink = styled(Link)`
  height: ${rem(48)};
  margin: ${rem(15)} 0 0;
  padding: ${rem(15)};
  width: 100%;
  max-width: ${rem(320)};
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  font-size: ${rem(14)};
  border-radius: ${rem(2)};
  margin: ${rem(32)} 0;
  display: flex;
  justify-content: center;
  align-items: center;
  text-decoration: none;
  font-weight: 500;
`;

const NoAccountWrapper = styled.span`
  ${secondaryText};
  display: flex;
  justify-content: center;
`;

const SingUpLink = styled(Link)`
  font-size: ${rem(14)};
  text-decoration: underline;
  ${secondaryText};
  display: inline-block;
  text-decoration: underline;
  margin: 0;
  margin-left: ${rem(4)};
`;

const ErrorWrapper = styled.div`
  color: red;
  margin-bottom: ${rem(15)};
`;

const SuccessTextWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  margin: ${rem(32)} 0;
  color: forestgreen;
  font-size: ${rem(18)};
`;

export { ForgotPasswordForm as DsiForgotPasswordForm };
